<template>
	<v-container
		class="relative mx-auto mt-4 flex flex-col items-center justify-center lg:w-225"
	>
		<!-- The right margin leaves space for the share button -->
		<v-h3 tag="h1" class="mr-12">
			{{ blokData.title }}
		</v-h3>

		<div class="absolute top-0 right-0">
			<button type="button" @click="copyUrl()">
				<v-icon icon="link" />
				{{ t("share") }}
			</button>
		</div>

		<nuxt-picture
			v-if="blokData.image?.filename"
			:src="blokData.image.filename"
			:alt="blokData.image.alt ?? blokData.title"
			provider="storyblok"
			loading="lazy"
			:width="sbImageDimensions(blokData.image.filename).width"
			:height="sbImageDimensions(blokData.image.filename).height"
			sizes="xs:100vw sm:640px md:768px lg:1024px xl:1280px 2xl:1536px"
			class="-mx-xs mt-xl block w-full md:mx-auto"
			:img-attrs="{
				class: 'object-cover w-full',
			}"
		/>

		<div v-if="publishDateStr || estimatedReadingTime" class="h-lg" />

		<div v-if="publishDateStr" class="text-gray-800">
			{{ publishDateStr }}
		</div>

		<div v-if="estimatedReadingTime" class="text-gray-800">
			{{ t("reading-time") }}: {{ estimatedReadingTime.minutes }}
			{{ t("minute") }}
		</div>

		<div class="mx-sm prose prose-sm mt-md md:mx-auto lg:prose-lg xl:prose-xl">
			<sb-rich-text :document="blokData.content" />
		</div>

		<div v-if="blokData.author" class="w-full text-right text-gray-800">
			{{ blokData.author }}
		</div>
	</v-container>

	<v-storyblok-article-recommendations
		v-if="showRecommendations"
		:content="recommendationsContent"
		:story="story"
	/>

	<div class="my-2xl bg-gray-50 px-sm md:px-0">
		<v-container class="space-y-lg py-lg">
			<v-h3>{{ t("read-also") }}</v-h3>

			<v-storyblok-article-feed-lite :feed="blokData.category" :story="story" />

			<div class="flex justify-center">
				<v-button
					theme="black"
					size="base"
					type="submit"
					class="text-center"
					:aria-label="t('read-more')"
					:to="`/${$t('storyblok.articles')}`"
				>
					{{ t("read-more") }}
				</v-button>
			</div>
		</v-container>
	</div>
</template>

<script setup lang="ts">
import {formatISODuration} from "date-fns";
import type {ISbStoryData} from "storyblok-js-client";
import {
	type SbRichTextDoc as ISbRichtext,
	type SbRichTextNode,
} from "@storyblok/js";
import {useClipboard, whenever} from "@vueuse/core";
import {useToast} from "@pkg/shared/store/toast";
import {
	type ProductRecommendationsContent,
	type FAQSectionBlokComponent,
} from "@pkg/shared/types";

import {useLocalization} from "@pkg/shared/composables/use-localization";
import {useReadingTime} from "@pkg/shared/composables/use-reading-time";
import {useStoryblokStory} from "@pkg/shared/composables/use-storyblok-story";
import {richTextToString} from "@pkg/shared/utils/rich-text-to-string";
import sbImageDimensions from "@pkg/shared/utils/sb-image-dimensions";

const props = defineProps<{
	story: ISbStoryData;
}>();

const {
	public: {appUrl},
} = useRuntimeConfig();

const {languageTag} = useLocalization();
const {copy, copied} = useClipboard();
const toast = useToast();

const blokData = computed(() => props.story.content);

const publishDateStr = computed(() => {
	if (!blokData.value.created) {
		return undefined;
	}
	try {
		// Lazy, not sure how it handles time zones
		return new Date(blokData.value.created).toLocaleDateString(["fi-FI"]);
	} catch {
		return undefined;
	}
});

const {t} = useI18n();

const {data: recommendations} = useStoryblokStory(
	"global/article-product-recommendations",
);

const {estimatedReadingTime, wordCount} = useReadingTime(
	blokData.value.content,
);

const recommendationsContent = computed(
	() => recommendations.value?.content,
) as ComputedRef<ProductRecommendationsContent>;

const currentCategories = computed(() => blokData.value.category[0] || "");

const showRecommendations = computed<boolean>(() => {
	return recommendationsContent.value?.article_categories.includes(
		currentCategories.value,
	);
});

function copyUrl() {
	copy(window.location.toString());
}

whenever(copied, () => {
	toast.add("success", t("link-copied"));
});

const title = computed(() => blokData.value.title || props.story.name);

const ingressText = computed(
	() =>
		richTextToString(blokData.value.ingress, {preventExtraSpaces: true}) ||
		blokData.value.content.meta?.description,
);

useSeoMeta({
	title,
	description: ingressText.value || undefined,
	ogTitle: blokData.value.title || props.story.name,
	ogDescription: ingressText.value || undefined,
	ogImage: blokData.value.image?.filename ?? undefined,
});

const absoluteUrl = new URL(props.story.full_slug, appUrl).href;

useJsonld({
	"@context": "https://schema.org",
	"@type": "BlogPosting",
	"@id": `${absoluteUrl}#article`,
	mainEntityOfPage: {"@id": `${absoluteUrl}#webpage`},
	headline: title.value,
	image: [blokData.value.image?.filename ?? undefined],
	inLanguage: languageTag,
	datePublished: props.story.published_at || undefined,
	dateModified: props.story.updated_at || undefined,
	author: {"@type": "Organization", name: "Masku Oy", url: appUrl},
	publisher: {"@type": "Organization", name: "Masku Oy", url: appUrl},
	keywords: blokData.value.category || [],
	wordCount,
	timeRequired: formatISODuration(estimatedReadingTime),
});

/**
 * Custom resolver to extract FAQ blocks from rich text
 */
function findFaqSections(
	document: ISbRichtext | string,
): FAQSectionBlokComponent[] {
	// Legacy rich text as HTML, ignore those
	if (typeof document === "string") {
		return [];
	}

	const faqBlocks: FAQSectionBlokComponent[] = [];

	const collectFaqBlocks = (node: SbRichTextNode) => {
		if (node.type === "blok") {
			const bloks = node.attrs?.body ?? [];

			for (const blok of bloks) {
				if (blok.component === "sb-faq-section") {
					faqBlocks.push(blok as unknown as FAQSectionBlokComponent);
				}
			}
		}

		if ("content" in node) {
			const children = node.content ?? [];

			for (const child of children) {
				collectFaqBlocks(child);
			}
		}
	};

	collectFaqBlocks(document);

	return faqBlocks;
}

// Add FAQPage schema if FAQ sections exist
useJsonld(() => {
	const sections = findFaqSections(blokData.value.content);
	if (sections.length === 0) {
		return null;
	}

	const allFaqItems = sections.flatMap((section) => section.items || []);

	return {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: allFaqItems
			.filter((item) => item.question && item.answer)
			.map((item) => ({
				"@type": "Question",
				name: richTextToString(item.question, {preventExtraSpaces: true}),
				acceptedAnswer: {
					"@type": "Answer",
					text: richTextToString(item.answer, {preventExtraSpaces: true}),
				},
			})),
	};
});
</script>

<i18n lang="json">
{
	"et": {
		"read-also": "Loe ka lisaks",
		"read-more": "Loe rohkem Masku blogist",
		"link-copied": "Link kopeeritud",
		"share": "Jaga",
		"reading-time": "Lugemisaeg",
		"minute": "min."
	},
	"fi": {
		"read-also": "Lue myös",
		"read-more": "Lue lisää Maskun blogista",
		"link-copied": "Linkki kopioitu",
		"share": "Jaa",
		"reading-time": "Lukuaika",
		"minute": "min."
	}
}
</i18n>
